import { NodeViewWrapper } from '@tiptap/react'
import { FileText, FolderOpen } from 'lucide-react'
import type { NodeViewProps } from '@tiptap/react'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { usePage, useProject, useTask } from '@/lib/db/rxdb/hooks'
import { useEntityLinkNavigation } from '@/hooks/useEntityLinkNavigation'
import { isClosedProgressType } from '@/routes/features/tasks/constants'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { resolveTaskIdentifier } from '@/routes/features/tasks/utils/taskIdentifier'
import { cn } from '@/lib/utils'

const CHIP_CLASS =
  'mx-px inline-flex max-w-full cursor-pointer items-center gap-1 rounded-md border bg-card px-1.5 py-px align-baseline text-[0.9em] leading-snug text-foreground transition-colors hover:bg-secondary'

/**
 * Live link chips inside docs and notes (design 7a/7b: "the embedded task
 * stays live and updates with its status"). A task shows its current status,
 * ID and name; a doc its current title. Click to open.
 */
export function EntityLinkChip({ node }: NodeViewProps) {
  const id = node.attrs.id as string | null
  const entityType = node.attrs.entityType as string | null
  const fallback = (node.attrs.label as string | null) ?? id ?? ''
  const openLink = useEntityLinkNavigation()

  const open = () => {
    if (id && entityType) void openLink(id, entityType)
  }

  let content = <span className="truncate">{fallback}</span>
  if (entityType === ENTITY_TYPES.WORK_ITEM) content = <TaskChipContent id={id} fallback={fallback} />
  else if (entityType === ENTITY_TYPES.DOCUMENT) content = <DocChipContent id={id} fallback={fallback} />
  else if (entityType === ENTITY_TYPES.PROJECT)
    content = (
      <>
        <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{fallback}</span>
      </>
    )

  return (
    <NodeViewWrapper
      as="span"
      data-entity-link=""
      data-entity-id={id ?? undefined}
      data-entity-type={entityType ?? undefined}
      className={CHIP_CLASS}
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter') open()
      }}
      contentEditable={false}
    >
      {content}
    </NodeViewWrapper>
  )
}

function TaskChipContent({ id, fallback }: { id: string | null; fallback: string }) {
  const { task } = useTask(id ?? undefined)
  const { project } = useProject(task?.projectId ?? undefined)
  const { all } = useStatuses()
  if (!task) return <span className="truncate">{fallback}</span>
  const progressType = all.statuses.find((s) => s.id === task.statusId)?.progressType ?? 'BL'
  const closed = isClosedProgressType(progressType)
  return (
    <>
      <StatusIcon progressType={progressType} statusId={task.statusId} size="sm" className="shrink-0" />
      <span className="shrink-0 font-mono text-[0.8em] text-muted-foreground">
        {resolveTaskIdentifier(task, project?.projectKey)}
      </span>
      <span className={cn('truncate', closed && 'text-muted-foreground line-through')}>{task.name}</span>
    </>
  )
}

function DocChipContent({ id, fallback }: { id: string | null; fallback: string }) {
  const { page } = usePage(id ?? undefined)
  return (
    <>
      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{page?.title || fallback}</span>
    </>
  )
}
